import React, { useEffect, useState } from 'react';
import { useTimeFilter } from '../contexts/TimeFilterContext';
import { useChartsAssignment, useMultiloadRoutes } from '../queries/useAssignmentChunks';
import { useAssignmentRouteEnabled } from '../hooks/useAssignmentRouteEnabled';
import { getSessionTripCounts } from '../utils/sessionTripCounts';
import { getUnassignedReasonChartKeys } from '../utils/normalizeUnassignedReason';
import { sumDeadheadMilesForTotal } from '../utils/deadheadEta';
import { formatTripRevenue } from '../utils/tripRevenue';
import {
  buildRevenueSummary,
  companyTimezone,
  highestUnassignedTrips,
  revenueByDriver,
  revenueByFleet,
  revenueByHour,
  revenueByLos,
  sessionIsFleetPlan,
  tripLosLabel,
  tripScheduleIso,
  unassignedReasonLabel,
  unassignedRevenueByReason,
} from '../utils/revenueAnalytics';
import DriverShiftLineup from '../components/DriverShiftLineup';
import { MapPin, CheckCircle, TriangleAlert, Activity, Truck, Navigation, Home, Clock, DollarSign, CircleDollarSign, Wallet } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

// Haversine distance calculation utility
const calculateHaversineDistance = (lat1, lng1, lat2, lng2) => {
  if (!lat1 || !lng1 || !lat2 || !lng2) return 0;
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

const calculateAwayFromHomeForDriver = (driver) => {
  const sortedTrips = [...(driver.trips || [])].sort((a, b) => (a.sequence ?? 999) - (b.sequence ?? 999));
  const lastTrip = sortedTrips[sortedTrips.length - 1];
  if (!lastTrip) return 0;
  const dropoffLat = lastTrip.dropoff_latitude || lastTrip.dropoff_lat;
  const dropoffLng = lastTrip.dropoff_longitude || lastTrip.dropoff_lng;
  if (!dropoffLat || !dropoffLng) return 0;
  let homeLat, homeLng;
  if (driver.takeMeHome && Array.isArray(driver.takeMeHome) && driver.takeMeHome.length >= 2) {
    homeLng = driver.takeMeHome[0];
    homeLat = driver.takeMeHome[1];
  } else {
    return 0;
  }
  if (!homeLat || !homeLng) return 0;
  return calculateHaversineDistance(dropoffLat, dropoffLng, homeLat, homeLng);
};

const getDriverTotalDistanceMiles = (driver) =>
  (driver.trips || []).reduce((sum, t) => sum + (Number(t.trip_distance_miles) || Number(t.trip_distance) || 0), 0);

const getDriverTotalTrips = (driver) =>
  (driver.trips || []).filter((t) => !t.is_break).length;

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// Mirrors :root tokens in src/index.css (Chart.js canvas cannot read CSS variables).
const THEME = {
  grid: 'rgba(226, 232, 240, 0.85)',
  tick: '#64748b',
  legend: '#464C57',
  ttBg: '#ffffff',
  ttTitle: '#0f172a',
  ttBody: '#64748b',
  ttBorder: '#E2E7F3',
  fontFamily: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
};

ChartJS.defaults.font.family = THEME.fontFamily;
ChartJS.defaults.color = THEME.tick;

const verticalGradientFill = (topColor, bottomColor) => (context) => {
  const { ctx, chartArea } = context.chart;
  if (!chartArea) return topColor;
  const g = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
  g.addColorStop(0, bottomColor);
  g.addColorStop(1, topColor);
  return g;
};

const defaultLegend = {
  display: true,
  position: 'top',
  align: 'start',
  labels: {
    color: THEME.legend,
    font: { size: 11, family: THEME.fontFamily, weight: '500' },
    boxWidth: 10,
    boxHeight: 10,
    padding: 14,
    usePointStyle: true,
    pointStyle: 'rectRounded',
  },
};

const defaultTooltip = {
  backgroundColor: THEME.ttBg,
  titleColor: THEME.ttTitle,
  bodyColor: THEME.ttBody,
  borderColor: THEME.ttBorder,
  borderWidth: 1,
  padding: 12,
  cornerRadius: 10,
  titleFont: { size: 12, weight: '600', family: THEME.fontFamily },
  bodyFont: { size: 11, family: THEME.fontFamily },
  boxPadding: 6,
  displayColors: true,
};

const makeChartOptions = (overrides = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  animation: { duration: 420, easing: 'easeOutQuart' },
  datasets: {
    bar: {
      categoryPercentage: 0.74,
      barPercentage: 0.82,
      borderRadius: 8,
      borderSkipped: false,
      maxBarThickness: 46,
      ...overrides.datasets?.bar,
    },
  },
  layout: {
    padding: { top: 8, right: 10, bottom: 6, left: 6 },
    ...overrides.layout,
  },
  plugins: {
    legend: { ...defaultLegend, ...(overrides.plugins?.legend || {}) },
    tooltip: { ...defaultTooltip, ...(overrides.plugins?.tooltip || {}) },
  },
  scales: {
    x: {
      grid: { display: false, drawBorder: false },
      border: { display: false },
      ticks: {
        color: THEME.tick,
        font: { size: 11, family: THEME.fontFamily },
        maxRotation: 45,
        minRotation: 0,
        autoSkip: true,
        maxTicksLimit: 14,
      },
      ...overrides.x,
    },
    y: {
      beginAtZero: true,
      grid: { color: THEME.grid, borderDash: [5, 5], drawTicks: false },
      border: { display: false },
      ticks: {
        color: THEME.tick,
        font: { size: 11, family: THEME.fontFamily },
        padding: 8,
      },
      ...overrides.y,
    },
  },
});

const makeHorizontalBarOptions = (overrides = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y',
  interaction: { mode: 'index', intersect: false },
  animation: { duration: 420, easing: 'easeOutQuart' },
  datasets: {
    bar: {
      categoryPercentage: 0.82,
      barPercentage: 0.88,
      borderRadius: 8,
      borderSkipped: false,
      maxBarThickness: 28,
      ...overrides.datasets?.bar,
    },
  },
  layout: {
    padding: { top: 4, right: 12, bottom: 4, left: 4 },
    ...overrides.layout,
  },
  plugins: {
    legend: { ...defaultLegend, display: false, ...(overrides.plugins?.legend || {}) },
    tooltip: { ...defaultTooltip, ...(overrides.plugins?.tooltip || {}) },
  },
  scales: {
    x: {
      beginAtZero: true,
      grid: { color: THEME.grid, borderDash: [5, 5], drawTicks: false },
      border: { display: false },
      ticks: {
        color: THEME.tick,
        font: { size: 11, family: THEME.fontFamily },
        precision: 0,
      },
      ...overrides.x,
    },
    y: {
      grid: { display: false, drawBorder: false },
      border: { display: false },
      ticks: {
        color: THEME.tick,
        font: { size: 11, family: THEME.fontFamily },
        autoSkip: false,
        padding: 6,
      },
      ...overrides.y,
    },
  },
});

const ChartCanvas = ({ className = '', style, fill, height, children }) => (
  <div
    style={height ? { height, ...style } : style}
    className={[
      'relative w-full min-w-0',
      height ? 'min-h-[268px]' : fill ? 'flex-1 min-h-[268px]' : 'min-h-[200px] h-[268px]',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
  >
    {children}
  </div>
);

function DriverMultiSelect({ drivers, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const list = drivers || [];
  const query = search.trim().toLowerCase();
  const visible = query
    ? list.filter((driver) => String(driver.driver_name || '').toLowerCase().includes(query))
    : list;
  let label = 'All drivers';
  if (selectedIds.size === 1) {
    const id = [...selectedIds][0];
    label = list.find((driver) => String(driver.driver_id) === id)?.driver_name || '1 driver';
  } else if (selectedIds.size > 1) {
    label = `${selectedIds.size} drivers`;
  }

  const toggle = (driverId) => {
    const id = String(driverId);
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  return (
    <div className="relative">
      <button
        type="button"
        className="flex items-center gap-2 rounded-md border border-card-border bg-white px-2.5 py-1.5 text-xs font-medium text-main-dark"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((value) => !value)}
      >
        <span className="max-w-[12rem] truncate">{label}</span>
        <svg className={`h-3.5 w-3.5 shrink-0 text-main-light transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="absolute right-0 top-full z-20 mt-1 flex max-h-80 w-64 flex-col rounded-lg border border-card-border bg-white py-2 shadow-xl">
            <div className="border-b border-card-border px-2 pb-2">
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search drivers"
                aria-label="Search drivers"
                className="w-full"
              />
            </div>
            <div className="flex items-center gap-2 border-b border-card-border px-3 py-1.5 text-xs text-main-light">
              <button
                type="button"
                className="hover:text-main-dark"
                onClick={() => onChange(new Set(list.map((driver) => String(driver.driver_id))))}
              >
                Select all
              </button>
              <span>|</span>
              <button
                type="button"
                className="hover:text-main-dark"
                onClick={() => onChange(new Set())}
              >
                Clear
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto py-1" role="listbox" aria-multiselectable="true">
              {visible.length === 0 ? (
                <p className="px-3 py-2 text-xs text-main-light">No drivers match.</p>
              ) : visible.map((driver) => {
                const id = String(driver.driver_id);
                const checked = selectedIds.has(id);
                return (
                  <label key={id} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs text-main-dark hover:bg-main-base-50">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(id)}
                    />
                    <span className="truncate">{driver.driver_name || id}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const SummaryCard = ({ icon: Icon, label, value, valueClass = 'text-dashboard-stats-primary', note }) => (
  <div className="bg-dashboard-stats-primary-light rounded-lg p-3 flex items-center gap-2.5">
    <div className="bg-dashboard-stats-primary-dark rounded-md p-1.5 flex-shrink-0">
      <Icon className="text-dashboard-stats-primary h-3.5 w-3.5" />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] text-dashboard-stats-info truncate uppercase font-medium">{label}</div>
      <div className={`text-xs font-bold tabular-nums ${valueClass}`}>{value}</div>
      {note ? <div className="text-[9px] text-dashboard-stats-info truncate">{note}</div> : null}
    </div>
  </div>
);

const ChartCard = ({ title, badge, action, children }) => (
  <div className="bg-white rounded-xl border border-card-border shadow-sm overflow-hidden h-full min-h-0 flex flex-col">
    <div className="flex items-center justify-between gap-2 px-5 pt-4 pb-3 border-b border-card-border shrink-0">
      <h3 className="text-sm font-semibold text-main-dark">{title}</h3>
      <div className="flex items-center gap-2 shrink-0">
        {action}
        {badge && (
          <span className="whitespace-nowrap text-[10px] font-medium text-dashboard-stats-primary bg-dashboard-stats-primary-light px-2 py-0.5 rounded-full tabular-nums">
            {badge}
          </span>
        )}
      </div>
    </div>
    <div className="px-4 pb-5 pt-3 flex-1 flex flex-col min-h-0">{children}</div>
  </div>
);

const ChartEmpty = ({ children }) => (
  <div className="flex flex-1 min-h-[268px] items-center justify-center rounded-lg border border-dashed border-card-border bg-main-base-50 text-sm text-main-light text-center px-4">
    {children}
  </div>
);

const formatAxisDollars = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  if (Math.abs(n) >= 1000) {
    const thousands = n / 1000;
    return `$${Math.abs(thousands) >= 10 ? thousands.toFixed(0) : thousands.toFixed(1)}k`;
  }
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(0)}`;
};

const dollarTick = {
  color: THEME.tick,
  font: { size: 11, family: THEME.fontFamily },
  padding: 8,
  callback: (value) => formatAxisDollars(value),
};

const assignedFill = verticalGradientFill('rgba(16, 185, 129, 0.9)', 'rgba(167, 243, 208, 0.4)');
const unassignedFill = verticalGradientFill('rgba(244, 63, 94, 0.88)', 'rgba(254, 205, 211, 0.45)');
const driverRevenueFill = verticalGradientFill('rgba(79, 70, 229, 0.92)', 'rgba(165, 180, 252, 0.38)');
const fleetRevenueFill = verticalGradientFill('rgba(14, 165, 233, 0.9)', 'rgba(186, 230, 253, 0.45)');

const ChartsPage = () => {
  const { selectedResultId, refreshAssignmentSession } = useTimeFilter();
  const [selectedDriverIds, setSelectedDriverIds] = useState(() => new Set());
  const [unassignedListLimit, setUnassignedListLimit] = useState('10');
  const [hourWindow, setHourWindow] = useState('3');
  const chartsRouteEnabled = useAssignmentRouteEnabled('charts');

  const chartsQuery = useChartsAssignment(selectedResultId, { enabled: chartsRouteEnabled });
  const multiloadQuery = useMultiloadRoutes(selectedResultId, { enabled: chartsRouteEnabled });
  const assignmentData = chartsQuery.normalized;

  const loading = chartsQuery.isLoading || (chartsQuery.isFetching && !assignmentData);

  const error = chartsQuery.error
    ? 'Failed to fetch selected assignment data'
    : (!loading && selectedResultId && !assignmentData ? 'Selected assignment data not found' : null);

  useEffect(() => {
    setSelectedDriverIds(new Set());
  }, [selectedResultId]);

  const handleRetry = () => {
    if (selectedResultId) refreshAssignmentSession(selectedResultId);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <span className="ml-3 text-sm text-textPrimary">Loading analytics…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-info-tint border border-info-border text-primary px-4 py-3 rounded text-sm">
        <strong>Error:</strong> {error}
        <button type="button" onClick={handleRetry} className="ml-4 btn-primary">
          Retry
        </button>
      </div>
    );
  }

  if (!assignmentData) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-bold text-textPrimary mb-2">No Data Selected</h2>
        <p className="text-sm text-textPrimary">Select a planning session from the header to view analytics.</p>
      </div>
    );
  }

  const summary = assignmentData.summary || {};
  const { assignedCount, unassignedCount, totalCount } = getSessionTripCounts(assignmentData);
  const assignmentRate = totalCount > 0 ? `${(assignedCount / totalCount * 100).toFixed(2)}%` : '0.00%';
  const planningTime = summary.planning_time_seconds ?? 0;

  const driversForCharts = selectedDriverIds.size === 0
    ? (assignmentData.drivers || [])
    : (assignmentData.drivers || []).filter((d) => selectedDriverIds.has(String(d.driver_id)));
  const multiloadRoutes =
    multiloadQuery.chunkMap?.multiload_routes ||
    assignmentData.multiload_routes ||
    {};

  const unassignedForCharts = assignmentData.unassigned_trips || [];

  const totalMiles = driversForCharts.reduce((sum, d) => sum + getDriverTotalDistanceMiles(d), 0);
  const totalDeadhead = driversForCharts.reduce((sum, d) =>
    sum + sumDeadheadMilesForTotal(d.trips), 0);
  const totalAwayFromHome = driversForCharts.reduce((sum, d) => sum + calculateAwayFromHomeForDriver(d), 0);

  const allDrivers = assignmentData.drivers || [];
  const timezone = companyTimezone(assignmentData);
  const fleetPlan = sessionIsFleetPlan(assignmentData);
  const revenue = buildRevenueSummary(allDrivers, unassignedForCharts);
  const losRows = revenueByLos(allDrivers, unassignedForCharts);
  const windowHours = (() => {
    const n = Math.floor(Number(hourWindow));
    return Number.isFinite(n) ? Math.max(1, Math.min(48, n)) : 3;
  })();
  const hourRows = revenueByHour(allDrivers, unassignedForCharts, timezone, windowHours);
  const reasonDollars = unassignedRevenueByReason(unassignedForCharts);
  const driverRevenueRows = revenueByDriver(driversForCharts);
  const fleetRevenueRows = fleetPlan ? revenueByFleet(allDrivers, timezone) : [];
  const ratedUnassigned = highestUnassignedTrips(unassignedForCharts, 'all');
  const topUnassigned = highestUnassignedTrips(unassignedForCharts, unassignedListLimit);
  const missingNote = revenue.missing === 1
    ? '1 trip has no rate'
    : revenue.missing > 1
      ? `${revenue.missing} trips have no rate`
      : '';
  const driverRevenueAvg = (() => {
    const rated = driverRevenueRows.filter((row) => row.hasRate);
    if (!rated.length) return null;
    return rated.reduce((sum, row) => sum + row.value, 0) / rated.length;
  })();

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-card-border shadow-sm p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-sm font-semibold text-main-dark">Trip Overview</h3>
          <DriverMultiSelect
            drivers={assignmentData.drivers || []}
            selectedIds={selectedDriverIds}
            onChange={setSelectedDriverIds}
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <SummaryCard icon={MapPin} label="Total Trips" value={totalCount} />
          <SummaryCard icon={CheckCircle} label="Assigned" value={assignedCount} />
          <SummaryCard
            icon={TriangleAlert}
            label="Unassigned"
            value={unassignedCount}
            valueClass="text-rose-500"
          />
          <SummaryCard icon={Activity} label="Assignment Rate" value={assignmentRate} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
          <SummaryCard
            icon={Wallet}
            label="Total revenue"
            value={formatTripRevenue(revenue.session)}
            note={missingNote}
          />
          <SummaryCard icon={DollarSign} label="Assigned revenue" value={formatTripRevenue(revenue.plan)} />
          <SummaryCard
            icon={CircleDollarSign}
            label="Unassigned revenue"
            value={formatTripRevenue(revenue.unassigned)}
            valueClass="text-rose-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        <ChartCard
          title="Revenue by Driver"
          badge={driverRevenueAvg == null ? '—' : `Avg ${formatTripRevenue(driverRevenueAvg)}`}
        >
          <MoneyByLabelChart
            rows={driverRevenueRows}
            empty="No drivers in this session."
            datasetLabel="Revenue"
            fill={driverRevenueFill}
            shortLabels
          />
        </ChartCard>

        <ChartCard title="Revenue by Level of Service" badge={`${losRows.length} types`}>
          <AssignedUnassignedChart
            rows={losRows}
            empty="No trip revenue in this session."
          />
        </ChartCard>

      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        <div className="relative min-h-0 lg:overflow-hidden">
          <div className="flex flex-col lg:absolute lg:inset-0">
            <ChartCard
              title="Highest Unassigned Trips"
              badge={`${topUnassigned.length} of ${ratedUnassigned.length}`}
              action={(
                <select
                  value={unassignedListLimit}
                  onChange={(e) => setUnassignedListLimit(e.target.value)}
                  className="no-theme h-6 w-auto min-w-[4.75rem] rounded-full border border-card-border bg-white pl-2.5 pr-6 text-xs font-medium leading-none text-main-dark shadow-none"
                  aria-label="How many unassigned trips to show"
                >
                  <option value="5">Top 5</option>
                  <option value="10">Top 10</option>
                  <option value="15">Top 15</option>
                  <option value="all">All</option>
                </select>
              )}
            >
              <HighestUnassignedList trips={topUnassigned} timezone={timezone} />
            </ChartCard>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <ChartCard title="Unassigned Revenue by Reason" badge={formatTripRevenue(revenue.unassigned)}>
            <ReasonRevenueChart rows={reasonDollars} />
          </ChartCard>

          <ChartCard
            title="Revenue by Window"
            action={(
              <label className="flex items-center gap-1.5 text-xs font-medium leading-4 text-main-light">
                Hours
                <input
                  type="number"
                  min={1}
                  max={48}
                  value={hourWindow}
                  onChange={(e) => setHourWindow(e.target.value)}
                  className="no-theme !box-border !h-[14px] !w-8 !py-0 !px-1 appearance-none rounded border border-card-border bg-white text-center !text-xs !font-semibold !leading-[12px] tabular-nums text-main-dark !shadow-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  aria-label="Hours in each pickup window"
                />
              </label>
            )}
          >
            <AssignedUnassignedChart
              rows={hourRows}
              empty="No pickup times with a rate."
            />
          </ChartCard>
        </div>
      </div>

      {fleetPlan ? (
        <ChartCard title="Revenue by Fleet" badge={`${fleetRevenueRows.length} windows`}>
          <MoneyByLabelChart
            rows={fleetRevenueRows}
            empty="No fleet window has a trip rate."
            datasetLabel="Revenue"
            fill={fleetRevenueFill}
            horizontal
          />
        </ChartCard>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        <ChartCard
          title="Total Distance by Driver"
          badge={`Avg ${(totalMiles / Math.max(driversForCharts.length, 1)).toFixed(1)} mi`}
        >
          <DriverDistanceChart drivers={driversForCharts} />
        </ChartCard>

        <ChartCard
          title="Deadhead Miles by Driver"
          badge={`Total ${totalDeadhead.toFixed(1)} mi`}
        >
          <DriverDeadheadChart drivers={driversForCharts} />
        </ChartCard>

        <ChartCard
          title="Trip Assignments by Driver"
          badge={`${driversForCharts.length} drivers`}
        >
          <DriverTripsChart drivers={driversForCharts} />
        </ChartCard>

        <ChartCard title="Unassigned Trips by Reason" badge={`${unassignedForCharts.length} trips`}>
          <UnassignedTripsChart unassignedTrips={unassignedForCharts} />
        </ChartCard>
      </div>

      <DriverShiftLineup drivers={driversForCharts} multiloadRoutes={multiloadRoutes} />

      <div className="bg-white rounded-xl border border-card-border shadow-sm p-4">
        <h3 className="text-sm font-semibold text-main-dark mb-3">Performance Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <SummaryCard icon={Truck} label="Total Miles" value={`${totalMiles.toFixed(1)} mi`} />
          <SummaryCard icon={Navigation} label="Deadhead Miles" value={`${totalDeadhead.toFixed(1)} mi`} />
          <SummaryCard icon={Home} label="Away from Home" value={`${totalAwayFromHome.toFixed(1)} mi`} />
          <SummaryCard
            icon={MapPin}
            label="Home↔Home Deadmile"
            value={`${(totalAwayFromHome + totalDeadhead).toFixed(1)} mi`}
          />
          <SummaryCard icon={Clock} label="Planning Time" value={`${planningTime.toFixed(1)}s`} />
        </div>
      </div>
    </div>
  );
};

const DriverDistanceChart = ({ drivers }) => {
  const list = drivers || [];
  if (list.length === 0) {
    return (
      <div className="flex flex-1 min-h-[268px] items-center justify-center rounded-lg border border-dashed border-card-border bg-main-base-50 text-sm text-main-light">
        No drivers in this session.
      </div>
    );
  }
  const distances = list.map((d) => getDriverTotalDistanceMiles(d));
  const data = {
    labels: list.map((d) => (d.driver_name || '').split(' ')[0]),
    datasets: [{
      label: 'Loaded miles',
      data: distances,
      backgroundColor: verticalGradientFill('rgba(79, 70, 229, 0.92)', 'rgba(165, 180, 252, 0.38)'),
      borderWidth: 0,
      hoverBorderWidth: 0,
    }],
  };
  return (
    <ChartCanvas fill>
      <Bar
        data={data}
        options={makeChartOptions({
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (c) => `${c.dataset.label}: ${c.parsed.y.toFixed(1)} mi`,
              },
            },
          },
        })}
      />
    </ChartCanvas>
  );
};

const DriverDeadheadChart = ({ drivers }) => {
  const list = drivers || [];
  if (list.length === 0) {
    return (
      <div className="flex flex-1 min-h-[268px] items-center justify-center rounded-lg border border-dashed border-card-border bg-main-base-50 text-sm text-main-light">
        No drivers in this session.
      </div>
    );
  }
  const deadheadMiles = list.map((d) => sumDeadheadMilesForTotal(d.trips));
  const data = {
    labels: list.map((d) => (d.driver_name || '').split(' ')[0]),
    datasets: [{
      label: 'Deadhead mi',
      data: deadheadMiles,
      backgroundColor: verticalGradientFill('rgba(244, 63, 94, 0.88)', 'rgba(254, 205, 211, 0.45)'),
      borderWidth: 0,
    }],
  };
  return (
    <ChartCanvas fill>
      <Bar data={data} options={makeChartOptions({ plugins: { legend: { display: false } } })} />
    </ChartCanvas>
  );
};

const DriverTripsChart = ({ drivers }) => {
  const list = drivers || [];
  if (list.length === 0) {
    return (
      <div className="flex flex-1 min-h-[268px] items-center justify-center rounded-lg border border-dashed border-card-border bg-main-base-50 text-sm text-main-light">
        No drivers in this session.
      </div>
    );
  }
  const tripCounts = list.map((d) => getDriverTotalTrips(d));
  const data = {
    labels: list.map((d) => (d.driver_name || '').split(' ')[0]),
    datasets: [{
      label: 'Trips',
      data: tripCounts,
      backgroundColor: verticalGradientFill('rgba(16, 185, 129, 0.9)', 'rgba(167, 243, 208, 0.4)'),
      borderWidth: 0,
    }],
  };
  return (
    <ChartCanvas fill>
      <Bar data={data} options={makeChartOptions({ plugins: { legend: { display: false } } })} />
    </ChartCanvas>
  );
};

const UnassignedTripsChart = ({ unassignedTrips }) => {
  const trips = unassignedTrips || [];
  if (trips.length === 0) {
    return (
      <div className="flex flex-1 min-h-[268px] items-center justify-center rounded-lg border border-dashed border-card-border bg-main-base-50 text-sm text-main-light">
        No unassigned trips.
      </div>
    );
  }
  const reasonCounts = {};
  trips.forEach((t) => {
    getUnassignedReasonChartKeys(t.reason).forEach((reason) => {
      reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
    });
  });
  const sorted = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1]).slice(0, 15);
  const data = {
    labels: sorted.map(([r]) => r),
    datasets: [{
      label: 'Phase failures',
      data: sorted.map(([, c]) => c),
      backgroundColor: verticalGradientFill('rgba(245, 158, 11, 0.9)', 'rgba(253, 230, 138, 0.45)'),
      borderWidth: 0,
    }],
  };
  return (
    <ChartCanvas fill>
      <Bar
        data={data}
        options={makeHorizontalBarOptions({
          plugins: {
            tooltip: {
              callbacks: {
                title: (items) => items[0]?.label ?? '',
                label: (ctx) => `${ctx.parsed.x} trip${ctx.parsed.x === 1 ? '' : 's'} failed in this phase`,
              },
            },
          },
          scales: {
            y: {
              ticks: {
                font: { size: 10, family: THEME.fontFamily },
              },
            },
          },
        })}
      />
    </ChartCanvas>
  );
};

const barValue = (ctx) => (
  ctx.chart?.options?.indexAxis === 'y' ? ctx.parsed.x : ctx.parsed.y
);

const MoneyByLabelChart = ({ rows, empty, datasetLabel, fill, horizontal = false, shortLabels = false }) => {
  const list = rows || [];
  if (!list.length) return <ChartEmpty>{empty}</ChartEmpty>;
  const data = {
    labels: list.map((row) => (shortLabels ? (row.label || '').split(' ')[0] : row.label)),
    datasets: [{
      label: datasetLabel,
      data: list.map((row) => row.value),
      backgroundColor: fill,
      borderWidth: 0,
    }],
  };
  const options = {
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => list[items[0]?.dataIndex]?.label ?? '',
          label: (ctx) => {
            const row = list[ctx.dataIndex];
            const amount = row?.hasRate === false ? null : barValue(ctx);
            return `${datasetLabel}: ${formatTripRevenue(amount)}`;
          },
        },
      },
    },
  };
  if (horizontal) {
    return (
      <ChartCanvas height={Math.max(268, list.length * 36)}>
        <Bar
          data={data}
          options={makeHorizontalBarOptions({
            ...options,
            x: { ticks: dollarTick },
          })}
        />
      </ChartCanvas>
    );
  }
  return (
    <ChartCanvas fill>
      <Bar data={data} options={makeChartOptions({ ...options, y: { ticks: dollarTick } })} />
    </ChartCanvas>
  );
};

const shownAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
};

const AssignedUnassignedChart = ({ rows, empty }) => {
  const list = (rows || []).filter((row) => shownAmount(row.assigned) != null || shownAmount(row.unassigned) != null);
  if (!list.length) return <ChartEmpty>{empty}</ChartEmpty>;
  const data = {
    labels: list.map((row) => row.label),
    datasets: [
      {
        label: 'Assigned',
        data: list.map((row) => shownAmount(row.assigned)),
        backgroundColor: assignedFill,
        borderWidth: 0,
      },
      {
        label: 'Unassigned',
        data: list.map((row) => shownAmount(row.unassigned)),
        backgroundColor: unassignedFill,
        borderWidth: 0,
      },
    ],
  };
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-3 flex items-center gap-8 px-1 text-[11px] font-medium text-[#464C57]">
        <span className="inline-flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-[3px] bg-[#10b981]" />
          Assigned
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-[3px] bg-[#f43f5e]" />
          Unassigned
        </span>
      </div>
      <ChartCanvas fill>
        <Bar
          data={data}
          options={makeChartOptions({
            y: { ticks: dollarTick },
            plugins: {
              legend: { display: false },
              tooltip: {
                filter: (ctx) => shownAmount(barValue(ctx)) != null,
                callbacks: {
                  label: (ctx) => `${ctx.dataset.label}: ${formatTripRevenue(barValue(ctx))}`,
                },
              },
            },
          })}
        />
      </ChartCanvas>
    </div>
  );
};

const ReasonRevenueChart = ({ rows }) => {
  const list = rows || [];
  if (!list.length) return <ChartEmpty>No unassigned trips with a rate.</ChartEmpty>;
  const data = {
    labels: list.map((row) => row.label),
    datasets: [{
      label: 'Revenue',
      data: list.map((row) => row.value),
      backgroundColor: verticalGradientFill('rgba(245, 158, 11, 0.9)', 'rgba(253, 230, 138, 0.45)'),
      borderWidth: 0,
    }],
  };
  return (
    <ChartCanvas height={Math.max(268, list.length * 36)}>
      <Bar
        data={data}
        options={makeHorizontalBarOptions({
          plugins: {
            tooltip: {
              callbacks: {
                title: (items) => items[0]?.label ?? '',
                label: (ctx) => `Unassigned revenue: ${formatTripRevenue(barValue(ctx))}`,
              },
            },
          },
          x: { ticks: dollarTick },
          y: {
            ticks: {
              color: THEME.tick,
              font: { size: 10, family: THEME.fontFamily },
              autoSkip: false,
            },
          },
        })}
      />
    </ChartCanvas>
  );
};

function formatPickup(iso, timezone) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone || 'America/Los_Angeles',
  });
}

const HighestUnassignedList = ({ trips, timezone }) => {
  const list = trips || [];
  if (!list.length) return <ChartEmpty>No unassigned trips with a rate.</ChartEmpty>;
  return (
    <div className="overflow-y-auto lg:h-0 lg:min-h-0 lg:flex-1">
      <ul className="divide-y divide-card-border">
        {list.map((trip, index) => {
          const reason = unassignedReasonLabel(trip);
          return (
            <li key={trip.trip_id || trip.id || index} className="flex items-start justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <div className="text-xs font-semibold text-main-dark truncate">
                  {trip.client_name || trip.trip_id || 'Trip'}
                </div>
                <div className="text-[10px] text-main-light truncate">
                  {formatPickup(tripScheduleIso(trip), timezone)} · {tripLosLabel(trip)}
                </div>
                <div className="text-[10px] text-main-secondary truncate" title={reason}>{reason}</div>
              </div>
              <div className="shrink-0 text-xs font-bold tabular-nums text-rose-500">
                {formatTripRevenue(trip)}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ChartsPage;
